import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import 'formiojs/dist/formio.full.css';
import { Form } from '@formio/react';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Circle, Arrow, Line } from 'react-konva';
import { BlobServiceClient } from '@azure/storage-blob';
import TemplateService from "../../../Services/TemplateService/TemplateService";
import { HandleApiError } from '../../../Utils/CommonErrors/HandleApiError';
import { hideLoader, showLoader } from '../../../Redux/Slice/Loader/loaderSlice';
import showToast from '../../../Utils/Toast/ToastNotification';
import "./StartInspection.css";
import ExcelJS from 'exceljs';
import { FaSquare, FaCircle, FaArrowRight, FaPencilAlt, FaFont } from 'react-icons/fa';
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { PageOrientation, Document, Paragraph, Packer, Table, TableRow, TableCell, ImageRun, HeadingLevel, AlignmentType, VerticalAlign, TextRun } from 'docx';

import mammoth from "mammoth";
import html2pdf from "html2pdf.js";
import logo from '../../../Assets/images/banner-logo.png'

import { PDFDocument, rgb } from 'pdf-lib';
import autoTable from 'jspdf-autotable'
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import inspectionService from '../../../Services/inspectionService/inspectionService';
import * as XLSX from "xlsx";
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const StartInspection = () => {
    // Navigation and state setup
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { id } = useParams();
    const inspectionId = location.state?.inspectionId;
    const created_at = location.state?.start_time;
    const [inspectionDetail, setInspection] = useState(null)

    const token = useSelector(
        (state) => state.auth.user.data?.body?.data?.token
    );

    // Refs
    const formRef = useRef(null);
    const stageRef = useRef(null);
    const filesQueueRef = useRef([]);
    const currentFileIndexRef = useRef(0);
    const resolveRef = useRef(null);
    const annotationHistoryRef = useRef([]); // Stack to store annotation history for undo

    // State
    const [formSchema, setFormSchema] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [showAnnotationModal, setShowAnnotationModal] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [annotations, setAnnotations] = useState([]);
    const [currentAnnotation, setCurrentAnnotation] = useState(null);
    const [imageSize, setImageSize] = useState({ width: 500, height: 500 });
    const [imageElement, setImageElement] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [totalFiles, setTotalFiles] = useState(0);
    const [currentFileNumber, setCurrentFileNumber] = useState(0);

    const [selectedShape, setSelectedShape] = useState('rectangle'); // 'rectangle' or 'circle'
    const [circleRadius, setCircleRadius] = useState(50);

    const [isDrawing, setIsDrawing] = useState(false);

    const [selectedColor, setSelectedColor] = useState('#000000'); // Default color: black

    const [selectedImage, setSelectedImage] = useState(null);

    const userId = useSelector(state => state.auth.user.data?.body?.data?.userData?.id);
    const azureAccountName = process.env.REACT_APP_AZURE_ACCOUNT_NAME_UAT;
    const azureContainerName = process.env.REACT_APP_AZURE_BLOB_CONTAINER_NAME_UAT;
    const azureSasToken = process.env.REACT_APP_AZURE_SAS_TOKEN_UAT;

    // Image loading
    const loadImage = useCallback((file) => {
        return new Promise((resolve) => {
            const url = URL.createObjectURL(file);
            const img = new window.Image();
            img.src = url;
            img.onload = () => {
                const maxWidth = 800;
                const maxHeight = 600;
                let width = img.width; 
                let height = img.height;

                if (width > maxWidth) {
                    const ratio = maxWidth / width;
                    width = maxWidth;
                    height = height * ratio;
                }

                if (height > maxHeight) {
                    const ratio = maxHeight / height;
                    height = maxHeight;
                    width = width * ratio;
                }

                setImageSize({ width, height });
                setImageElement(img);
                resolve();
            };
        });
    }, []);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const loadPDF = async (file, pageNumber = 1) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            setTotalPages(pdf.numPages); // Store total pages
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1.0 });

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({
                canvasContext: context,
                viewport: viewport,
            }).promise;

            const image = new Image();
            image.src = canvas.toDataURL('image/png');

            return new Promise((resolve, reject) => {
                image.onload = () => {
                    setImageElement(image);
                    setImageSize({ width: viewport.width, height: viewport.height });
                    resolve(image);
                };
                image.onerror = reject;
            });
        } catch (error) {
            throw error;
        }
    };

    // Process next file in queue
    const processNextFile = useCallback(async () => {
        if (currentFileIndexRef.current < filesQueueRef.current.length) {
            const file = filesQueueRef.current[currentFileIndexRef.current];
            setCurrentFile(file);
            setCurrentFileNumber(currentFileIndexRef.current + 1);
            setAnnotations([]); // Clear annotations for the new file
            setCurrentAnnotation(null); // Reset current annotation
            annotationHistoryRef.current = []; // Clear annotation history
            setIsProcessing(false); // Reset processing state

            try {
                if (file.type.startsWith('image/')) {
                    await loadImage(file); // Load image
                } else if (file.type === 'application/pdf') {
                    await loadPDF(file); // Load PDF
                } else {
                    throw new Error('Unsupported file type');
                }
                setShowAnnotationModal(true); // Show modal after file is loaded
            } catch (error) {
                showToast('error', `Failed to load ${file.type.startsWith('image/') ? 'image' : 'PDF'} for annotation`);
                currentFileIndexRef.current++;
                await processNextFile();
            }
        } else {
            setShowAnnotationModal(false);
            setCurrentFile(null);
            setImageElement(null);
            setImageSize({ width: 500, height: 500 });
            if (resolveRef.current) {
                resolveRef.current(filesQueueRef.current);
                resolveRef.current = null;
            }
            filesQueueRef.current = [];
            currentFileIndexRef.current = 0;
            setTotalFiles(0);
            setCurrentFileNumber(0);
        }
    }, [loadImage, loadPDF]);


    // Annotation handlers Mouse Events For Annotation
    const handleMouseDown = useCallback(
        (e) => {
            const stage = e.target.getStage();
            const pointerPos = stage.getPointerPosition();

            if (selectedShape === 'text') {
                const annotationText = window.prompt('Enter annotation text:');
                if (annotationText) {
                    const newAnnotation = {
                        x: pointerPos.x,
                        y: pointerPos.y,
                        text: annotationText,
                        id: Date.now().toString(),
                        shape: 'text',
                        color: selectedColor, // Use selected color
                    };
                    setAnnotations((prev) => [...prev, newAnnotation]);
                }
            } else if (selectedShape === 'pencil') {
                setCurrentAnnotation({
                    shape: 'pencil',
                    points: [pointerPos.x, pointerPos.y],
                    text: '',
                    id: Date.now().toString(),
                    color: selectedColor, // Use selected color
                });
                setIsDrawing(true);
            } else if (selectedShape === 'arrow') {
                setCurrentAnnotation({
                    x: pointerPos.x,
                    y: pointerPos.y,
                    endX: pointerPos.x,
                    endY: pointerPos.y,
                    text: '',
                    id: Date.now().toString(),
                    color: selectedColor, // Use selected color
                });
                setIsDrawing(true);
            } else {
                setCurrentAnnotation({
                    x: pointerPos.x,
                    y: pointerPos.y,
                    width: 0,
                    height: 0,
                    text: '',
                    id: Date.now().toString(),
                    color: selectedColor, // Use selected color
                });
                setIsDrawing(true);
            }
        },
        [selectedShape, selectedColor]
    );

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDrawing || !currentAnnotation) return;
            const stage = e.target.getStage();
            const pointerPos = stage.getPointerPosition();

            if (selectedShape === 'pencil') {
                setCurrentAnnotation((prev) => ({
                    ...prev,
                    points: [...prev.points, pointerPos.x, pointerPos.y],
                }));
            } else if (selectedShape === 'arrow') {
                setCurrentAnnotation((prev) => ({
                    ...prev,
                    endX: pointerPos.x,
                    endY: pointerPos.y,
                }));
            } else if (selectedShape === 'rectangle') {
                setCurrentAnnotation((prev) => ({
                    ...prev,
                    width: pointerPos.x - prev.x,
                    height: pointerPos.y - prev.y,
                }));
            } else if (selectedShape === 'circle') {
                const dx = pointerPos.x - currentAnnotation.x;
                const dy = pointerPos.y - currentAnnotation.y;
                const radius = Math.sqrt(dx * dx + dy * dy);
                setCircleRadius(radius);
            }
        },
        [isDrawing, currentAnnotation, selectedShape]
    );

    const handleMouseUp = useCallback(() => {
        if (!currentAnnotation) return;

        let annotationText = '';
        if (selectedShape !== 'pencil') {
            annotationText = window.prompt('Enter annotation text:');
        }

        const newAnnotation = {
            ...currentAnnotation,
            text: annotationText,
            shape: selectedShape,
            radius: selectedShape === 'circle' ? circleRadius : undefined,
            color: selectedColor, // Use selected color
        };

        setAnnotations((prev) => {
            const updatedAnnotations = [...prev, newAnnotation];
            annotationHistoryRef.current = updatedAnnotations;
            return updatedAnnotations;
        });

        setCurrentAnnotation(null);
        setIsDrawing(false);
    }, [currentAnnotation, selectedShape, circleRadius, selectedColor]);


    // Undo the last annotation
    const undoAnnotation = useCallback(() => {
        if (annotationHistoryRef.current.length > 0) {
            const updatedAnnotations = [...annotationHistoryRef.current];
            updatedAnnotations.pop(); // Remove the last annotation
            annotationHistoryRef.current = updatedAnnotations; // Update history
            setAnnotations(updatedAnnotations); // Update annotations state
        }
    }, []);

    // Save annotated image
    const saveAnnotatedImage = useCallback(async () => {
        if (!stageRef.current || !currentFile) return currentFile;

        try {
            setIsProcessing(true);
            const dataUrl = stageRef.current.toDataURL({
                mimeType: 'image/png',
                quality: 1
            });

            const response = await fetch(dataUrl);
            const blob = await response.blob();

            return new File([blob], currentFile.name, {
                type: 'image/png',
                lastModified: Date.now()
            });
        } catch (error) {
            return currentFile;
        } finally {
            setIsProcessing(false);
        }
    }, [currentFile]);

    // Handle annotation completion
    const handleAnnotationComplete = useCallback(async (shouldSave) => {
        try {
            setIsProcessing(true);
            let resultFile = currentFile;
            if (shouldSave) {
                resultFile = await saveAnnotatedImage();
            }

            // Update the file in queue
            filesQueueRef.current[currentFileIndexRef.current] = resultFile;
            currentFileIndexRef.current++;

            // Process the next file
            await processNextFile();
        } catch (error) {
            showToast('error', 'Error processing annotation');
            currentFileIndexRef.current++;
            await processNextFile();
        } finally {
            setIsProcessing(false);
        }
    }, [currentFile, saveAnnotatedImage, processNextFile]);

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result); // this will be a base64 string
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Old File service using azure storage type 
    // const fileService = useRef({
    //     uploadFile: async (fileName, file, dir, progressCallback) => {
    //         try {
    //             if (file.type.startsWith('image/') || file.type === 'application/pdf') {
    //                 filesQueueRef.current = [file];
    //                 setTotalFiles(1);
    //                 currentFileIndexRef.current = 0;

    //                 const annotatedFiles = await new Promise((resolve) => {
    //                     resolveRef.current = resolve;
    //                     processNextFile();
    //                 });

    //                 const annotatedFile = annotatedFiles[0] || file;

    //                 const blobServiceClient = new BlobServiceClient(
    //                     `https://${azureAccountName}.blob.core.windows.net?${azureSasToken}`
    //                 );
    //                 const containerClient = blobServiceClient.getContainerClient(azureContainerName);
    //                 const blobName = `${Date.now()}-${annotatedFile.name}`;
    //                 const blockBlobClient = containerClient.getBlockBlobClient(blobName);

    //                 await blockBlobClient.uploadBrowserData(annotatedFile, {
    //                     blobHTTPHeaders: { blobContentType: annotatedFile.type },
    //                     onProgress: progressCallback,
    //                 });

    //                 const base64 = await fileToBase64(file);

    //                 return {
    //                     storage: 'azure',
    //                     name: fileName,
    //                     size: annotatedFile.size,
    //                     type: annotatedFile.type,
    //                     url: blockBlobClient.url,
    //                     base64: base64,
    //                     annotations: annotations.length > 0 ? annotations : undefined,
    //                 };
    //             }

    //             // Handle other file types (no annotation)
    //             const blobServiceClient = new BlobServiceClient(
    //                 `https://${azureAccountName}.blob.core.windows.net?${azureSasToken}`
    //             );
    //             const containerClient = blobServiceClient.getContainerClient(azureContainerName);
    //             const blobName = `${Date.now()}-${file.name}`;
    //             const blockBlobClient = containerClient.getBlockBlobClient(blobName);

    //             await blockBlobClient.uploadBrowserData(file, {
    //                 blobHTTPHeaders: { blobContentType: file.type },
    //                 onProgress: progressCallback,
    //             });
    //             const base64 = await fileToBase64(file);

    //             return {
    //                 storage: 'azure',
    //                 name: fileName,
    //                 size: file.size,
    //                 type: file.type,
    //                 url: blockBlobClient.url,
    //                 base64: base64,
    //             };
    //         } catch (error) {
    //             console.error("Upload Error: ", error);
    //             throw error;
    //         }
    //     },
    //     downloadFile: async (file) => {
    //         try {
    //             if (!file || !file.url) {
    //                 throw new Error('Invalid file or missing URL');
    //             }
    //             return Promise.resolve(file);
    //         } catch (error) {
    //             console.error('Download Error:', error);
    //             throw error;
    //         }
    //     },
    // }).current;

    // New one using Base64
    const fileService = useRef({
        uploadFile: async (fileName, file, dir, progressCallback) => {
            try {
                // Process annotation (if any)
                filesQueueRef.current = [file];
                setTotalFiles(1);
                currentFileIndexRef.current = 0;

                const annotatedFiles = await new Promise((resolve) => {
                    resolveRef.current = resolve;
                    processNextFile();
                });

                const annotatedFile = annotatedFiles[0] || file;

                // Convert annotated file to base64
                const base64 = await fileToBase64(annotatedFile);

                return {
                    storage: 'base64',
                    name: fileName,
                    size: annotatedFile.size,
                    type: annotatedFile.type,
                    url: base64, // ✅ direct base64 data URL
                    //   base64: base64, // optional (Form.io uses this)
                    annotations: annotations.length > 0 ? annotations : undefined,
                };
            } catch (error) {
                throw error;
            }
        },

        downloadFile: async (file) => {
            try {
                if (!file || !file.url) {
                    throw new Error('Invalid file or missing URL');
                }
                // Item 8: entries synced from the iPad carry an unsigned url to
                // a private container (storage 'synced'/'local' — app labels
                // Form.io has no provider for), which rendered as dead
                // filename rows. Hand Form.io a fetchable signed link under a
                // storage type it understands.
                if (file.url.startsWith('https://') && resolveSignedUrlRef.current) {
                    const signed = await resolveSignedUrlRef.current(file.url);
                    return { ...file, url: signed, storage: 'url' };
                }
                return Promise.resolve(file); // base64 URL already available
            } catch (error) {
                throw error;
            }
        },
    }).current;


    const [formData, setformData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReponseExist, setIsReponseExist] = useState(false);
    // Template handling
    const getTemplate = useCallback(async () => {
        dispatch(showLoader());
        try {
            setIsLoading(true);
            const response = await TemplateService.getTemplate({ id }, token);
            const template = response?.data?.body?.data?.templates[0];
            setFormSchema(template.form_data);

            try {
                const result = await TemplateService.getTemplateResponse({
                    template_id: id,
                    inspection_id: inspectionId,
                }, token);
                const existingRes = result?.data?.body?.data;
                setformData(existingRes)
                if (existingRes?.form_data) {
                    const values = {};
                    existingRes.form_data.forEach((field) => {
                        values[field.key] = field.value;
                    });
                    setFormValues(values);

                    // Wait for form to be ready before setting submission
                    if (formRef.current?.formio) {
                        formRef.current.formio.ready.then(() => {
                            formRef.current.formio.setSubmission({ data: values });
                        });
                    }
                } else {
                    setFormValues({});
                }
            } catch (innerErr) {
                if (innerErr?.response?.data?.body?.message !== 'response not found') {
                    HandleApiError(innerErr);
                }
            }
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsLoading(false);
            dispatch(hideLoader());
        }
    }, [id, inspectionId, token]);



    const handleSubmit = async (submission) => {
        dispatch(showLoader());
        try {
            const isDraftSave = submission?.state === "draft"
            const response = await TemplateService.saveResponse({
                id,
                form_data: submission.data,
                user_id: userId,
                inspection_id: inspectionId,
                created_at: created_at,
                updated_at: new Date(),
                source: 'web',
                ...(isDraftSave ? { status: "In Progress" } : {}),
            }, token);

            if (response?.data?.statusCode === 200 && response.data.body?.data.state === "draft") {
                const saveButtons = document.querySelectorAll('button');

                setTimeout(() => {
                    saveButtons.forEach((btn) => {
                        const spinner = btn.querySelector('.fa-refresh');
                        if (spinner) {
                            spinner.classList.remove('fa-spin');  // Stop spinning
                            spinner.remove();                     // Remove icon element
                        }
                    });
                }, 200);
                showToast("success", response.data.body?.message);
            } else if (response?.data?.statusCode === 200 && response.data.body?.data.state === 'submit') {
                showToast("success", response.data.body?.message);
                navigate(-1)
            } else {
                showToast("error", response.data.body?.message);
            }
        } catch (error) {
            HandleApiError(error);
        } finally {
            dispatch(hideLoader());
        }
    };


    // ── Item 8 (UAT B6): signed photo links ─────────────────────────────────
    //
    // Photos live in a PRIVATE blob container; the urls stored in form data are
    // unsigned, which is why they rendered as broken icons and every export
    // fetch came back 403. The backend exchanges those urls for short-lived
    // read links (getPhotoAccessUrls). Signing lives ONLY in this render/export
    // layer: nothing signed is ever written into formValues or saved back —
    // a signed url expires within the hour and would poison stored data.

    /** canonical url → signed url, plus the batch's expiry. State mirror
     * triggers re-renders; the ref is the synchronous source of truth. */
    const [signedUrlMap, setSignedUrlMap] = useState({});
    const signedUrlsRef = useRef({ map: {}, expiresAt: 0, inFlight: null });

    /** Refuse to serve a signed url within 5 minutes of its expiry, so a long
     * export never starts a fetch on a link about to lapse. */
    const SIGNED_URL_STALE_MARGIN_MS = 5 * 60 * 1000;
    /** Server-side cap per exchange request. */
    const MAX_SIGN_BATCH = 256;

    const isCloudPhotoUrl = (u) =>
        typeof u === "string" && u.startsWith("https://") && !u.startsWith("data:");

    /** Every cloud photo url reachable in a form-data tree (exports walk nested
     * structures, so this recurses fully — unlike the top-level gallery walk). */
    const collectPhotoUrls = (node, found = new Set()) => {
        if (Array.isArray(node)) {
            for (const item of node) collectPhotoUrls(item, found);
        } else if (node && typeof node === "object") {
            if (isCloudPhotoUrl(node.url)) found.add(node.url);
            for (const [key, value] of Object.entries(node)) {
                if (key !== "url") collectPhotoUrls(value, found);
            }
        }
        return [...found];
    };

    /** Exchange canonical urls for signed ones. Failures leave entries
     * unsigned — callers fall back to the raw url, which is exactly the
     * pre-item-8 behaviour, never worse. Concurrent callers share one flight. */
    const exchangePhotoUrls = useCallback(async (urls) => {
        const wanted = [...new Set(urls.filter(isCloudPhotoUrl))];
        if (wanted.length === 0) return signedUrlsRef.current.map;
        if (signedUrlsRef.current.inFlight) {
            await signedUrlsRef.current.inFlight;
            return signedUrlsRef.current.map;
        }

        const flight = (async () => {
            const merged = { ...signedUrlsRef.current.map };
            let expiresAt = signedUrlsRef.current.expiresAt;
            for (let i = 0; i < wanted.length; i += MAX_SIGN_BATCH) {
                const chunk = wanted.slice(i, i + MAX_SIGN_BATCH);
                try {
                    const response = await TemplateService.getPhotoAccessUrls({ urls: chunk }, token);
                    const data = response?.data?.body?.data;
                    if (data?.urls) {
                        for (const [canonical, signed] of Object.entries(data.urls)) {
                            if (signed) merged[canonical] = signed;
                        }
                        const parsed = Date.parse(data.expires_at);
                        if (Number.isFinite(parsed)) expiresAt = parsed;
                    }
                } catch (err) {
                    console.warn("Photo url signing failed — falling back to raw urls:", err);
                }
            }
            signedUrlsRef.current = { map: merged, expiresAt, inFlight: null };
            setSignedUrlMap(merged);
        })();

        signedUrlsRef.current.inFlight = flight;
        await flight;
        return signedUrlsRef.current.map;
    }, [token]);

    /** Sign everything in formValues whenever it changes (one batch round-trip
     * for the whole inspection — the gallery then renders signed <img> srcs). */
    useEffect(() => {
        const urls = collectPhotoUrls(formValues);
        const fresh =
            Date.now() < signedUrlsRef.current.expiresAt - SIGNED_URL_STALE_MARGIN_MS;
        const missing = urls.filter((u) => !fresh || !signedUrlsRef.current.map[u]);
        if (missing.length > 0) {
            exchangePhotoUrls(urls);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues, exchangePhotoUrls]);

    /** The url to actually fetch/render for a stored canonical url. Re-signs
     * the whole known batch when the map has gone stale (long exports). */
    const resolveSignedUrl = async (url) => {
        if (!isCloudPhotoUrl(url)) return url;
        const { map, expiresAt } = signedUrlsRef.current;
        const fresh = Date.now() < expiresAt - SIGNED_URL_STALE_MARGIN_MS;
        if (fresh && map[url]) return map[url];
        const refreshed = await exchangePhotoUrls(
            [...new Set([...collectPhotoUrls(formValues), url])],
        );
        return refreshed[url] ?? url;
    };

    /** Render-time (synchronous) variant for <img src>: best signed url known
     * right now, falling back to the raw one. */
    const displayUrl = (url) =>
        (isCloudPhotoUrl(url) && signedUrlMap[url]) || url;

    /** Stable handle for closures created once (fileService lives in a useRef,
     * so it must not capture a first-render resolveSignedUrl). */
    const resolveSignedUrlRef = useRef(null);
    resolveSignedUrlRef.current = resolveSignedUrl;

    // Export Excel Functionality

    const fetchImageBuffer = async (url) => {
        // Item 8: private container — fetch over the signed link.
        const response = await fetch(await resolveSignedUrl(url));
        if (!response.ok) throw new Error(`Failed to fetch image from ${url}`);
        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    };

    const formatKey = (key) =>
        key.replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    // Export Functionalities PDF/EXCEL/WORD

    const exportToExcelWithImages = async (formDataValue,) => {

        dispatch(showLoader());
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Inspection Data");

        // === 🏗️ HEADER SECTION ===
        // Load basic Inspection Details and insert logo
        const response = await fetch(logo);
        const logoBuffer = await response.arrayBuffer();

        const logoImageId = workbook.addImage({
            buffer: logoBuffer,
            extension: 'png'
        });

        // Set column widths for header section
        worksheet.getColumn(1).width = 25;
        worksheet.getColumn(2).width = 60;

        // Add logo to top-left cell (A1)
        worksheet.mergeCells('A1:A4'); // Merge for a nice logo area
        worksheet.addImage(logoImageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 90, height: 60 }
        });

        // Add Inspection Details starting below logo
        const headerStartRow = 5;
        const inspectionName = inspectionDetail?.inspection_name || 'N/A';
        const inspector = inspectionDetail?.inspectors?.[0]
            ? `${inspectionDetail.inspectors[0].first_name} ${inspectionDetail.inspectors[0].last_name}`
            : 'N/A';
        const projectAddress = `${inspectionDetail?.project_streetaddress || ''} ${inspectionDetail?.project_suburb || ''} ${inspectionDetail?.state_name || ''} ${inspectionDetail?.project_postcode || ''}`.trim() || 'N/A';

        // downloaded file name
        const filename = `${inspectionName} - ${projectAddress}.xlsx`;

        const headerDetails = [
            ['Inspection Name:', inspectionName],
            ['Inspector:', inspector],
            ['Project Address:', projectAddress]
        ];

        headerDetails.forEach((item, i) => {
            const row = worksheet.getRow(headerStartRow + i);
            row.getCell(1).value = item[0];
            row.getCell(2).value = item[1];
            row.getCell(1).font = { bold: true, size: 11 };
            row.getCell(2).font = { size: 11 };
            row.getCell(1).alignment = { vertical: 'middle' };
            row.getCell(2).alignment = { vertical: 'middle' };
        });

        // Add one blank row before starting existing logic
        const nextRow = headerStartRow + headerDetails.length + 1;
        worksheet.addRow([]);
        let currentRow = nextRow;

        // === 🧩 Your existing logic continues from here ===
        const keyToLabel = createKeyToLabelMap(formDataValue);

        // Step 1: Add top-level fields
        const topLevelFields = Object.keys(formDataValue).filter(
            key =>
                key !== "submit" &&
                !Array.isArray(formDataValue[key]) &&
                typeof formDataValue[key] !== "object"
        );

        topLevelFields.forEach((key) => {
            const label =
                keyToLabel[key] ||
                key
                    .replace(/([A-Z])/g, " $1")
                    .toLowerCase()
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
            const value = formDataValue[key];
            const row = worksheet.addRow([label + ":", value]);
            row.getCell(1).font = { size: 10, bold: true };
            row.getCell(2).font = { size: 10 };
            currentRow++;
        });

        // Step 2: Function to flatten nested objects
        const flattenData = (obj) => {
            const result = {};
            for (const [key, value] of Object.entries(obj)) {
                if (Array.isArray(value) && value.length && typeof value[0] === 'object') {
                    if (value[0].url) {
                        result[key] = value;
                    } else {
                        value.forEach(item => {
                            Object.assign(result, flattenData(item));
                        });
                    }
                } else if (typeof value === 'object' && value !== null) {
                    Object.assign(result, flattenData(value));
                } else {
                    result[key] = value;
                }
            }
            return result;
        };

        // Step 3: Find all top-level keys that are arrays of objects
        const arrayKeys = Object.keys(formDataValue).filter(
            key => key !== "submit" && Array.isArray(formDataValue[key]) && formDataValue[key].length && typeof formDataValue[key][0] === 'object'
        );

        // Step 4: Process each array key and create a table
        for (const dynamicWord of arrayKeys) {
            const formattedDynamicWord = keyToLabel[dynamicWord] || dynamicWord
                .replace(/([A-Z])/g, ' $1')
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            worksheet.addRow([`${formattedDynamicWord}: `]);
            currentRow++;

            const allRows = [];
            const allHeaders = new Set();
            const imageColumns = [];
            let maxImages = 0;

            if (formDataValue[dynamicWord] && Array.isArray(formDataValue[dynamicWord])) {
                formDataValue[dynamicWord].forEach(item => {
                    const flat = flattenData(item);
                    Object.keys(flat).forEach(k => {
                        allHeaders.add(k);
                        if (Array.isArray(flat[k]) && flat[k][0]?.url) {
                            imageColumns.push(k);
                        }
                    });
                    allRows.push(flat);
                });
            }

            const regularColumns = Array.from(allHeaders).filter(header => !imageColumns.includes(header));
            const sortedHeaders = [...regularColumns];

            // Find the maximum number of images in any row for this table
            allRows.forEach(row => {
                imageColumns.forEach(col => {
                    if (Array.isArray(row[col])) {
                        maxImages = Math.max(maxImages, row[col].length);
                    }
                });
            });

            // Use all image column keys dynamically (so multiple image fields like photos, rectifiedPhotos are included)
            const imageHeaderKeys = imageColumns.length > 0 ? Array.from(new Set(imageColumns)) : [];
            // Create formatted labels for each image header key
            const formattedImageHeaders = imageHeaderKeys.map(k => keyToLabel[k] || k
                .replace(/([A-Z])/g, ' $1')
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            );

            if (maxImages > 0 && imageHeaderKeys.length > 0) {
                sortedHeaders.push(...imageHeaderKeys);
            }

            // Define worksheet columns using the original keys
            worksheet.columns = sortedHeaders.map((key) => ({
                key: key,
                width: 30
            }));

            // Add the column names for this table, using labels instead of keys
            const displayHeaders = regularColumns.map(header => keyToLabel[header] || header
                .replace(/([A-Z])/g, ' $1')
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            );
            if (maxImages > 0 && imageHeaderKeys.length > 0) {
                displayHeaders.push(...formattedImageHeaders);
            }
            const columnNamesRow = worksheet.addRow(displayHeaders);
            columnNamesRow.font = { bold: true };
            columnNamesRow.alignment = { horizontal: 'center' };

            currentRow++;

            // Add the data rows for this table (below the headers)
            for (let i = 0; i < allRows.length; i++) {
                const rowData = {};
                regularColumns.forEach(header => {
                    rowData[header] = allRows[i][header] || '';
                });

                // Add placeholders for each image column
                if (imageHeaderKeys.length > 0) {
                    imageHeaderKeys.forEach(k => {
                        rowData[k] = '';
                    });
                }

                const row = worksheet.addRow(rowData);

                // Handle image columns - place images for each image column
                if (imageHeaderKeys.length > 0) {
                    for (let colIdx = 0; colIdx < imageHeaderKeys.length; colIdx++) {
                        const colKey = imageHeaderKeys[colIdx];
                        const images = allRows[i][colKey] || [];
                        if (!Array.isArray(images) || images.length === 0) continue;

                        const firstImageColIndex = regularColumns.length + colIdx;
                        const imageIdList = [];

                        for (const img of images) {
                            if (!img?.url) continue;
                            try {
                                const buffer = await fetchImageBuffer(img.url);
                                const ext = img.type ? img.type.split('/')?.pop() : (img.url.startsWith('data:image/png') ? 'png' : 'jpeg');
                                const imageId = workbook.addImage({
                                    buffer,
                                    extension: ext === 'jpg' ? 'jpeg' : ext
                                });
                                imageIdList.push(imageId);
                            } catch (err) {
                                await showToast("error", `Failed to fetch image buffer: ${err}`);
                            }
                        }

                        if (imageIdList.length > 0) {
                            worksheet.addImage(imageIdList[0], {
                                tl: { col: firstImageColIndex, row: row.number - 1 },
                                ext: { width: 100, height: 100 },
                            });
                            for (let j = 1; j < imageIdList.length; j++) {
                                worksheet.addImage(imageIdList[j], {
                                    tl: { col: firstImageColIndex, row: row.number - 1, x: j * 105 },
                                    ext: { width: 100, height: 100 },
                                });
                            }
                            row.height = Math.max(row.height || 0, 100); // Ensure row is tall enough
                        }
                    }
                }
            }

            if (arrayKeys.indexOf(dynamicWord) < arrayKeys.length - 1) {
                worksheet.addRow([]);
                currentRow++;
            }
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);

        // api call for report generated
        await reportGenerated('excel')

        link.click();
        document.body.removeChild(link);
        dispatch(hideLoader());
    };

    const exportToWordWithImages = async (formDataValue, filename = "inspection_data.docx") => {
        dispatch(showLoader());

        // Create key-to-label mapping
        const keyToLabel = createKeyToLabelMap(formDataValue);

        const docChildren = [];

        // 1. Add top-level fields
        const topLevelFields = Object.keys(formDataValue).filter(
            key => key !== "submit" && !Array.isArray(formDataValue[key]) && typeof formDataValue[key] !== "object"
        );

        topLevelFields.forEach(key => {
            const label = keyToLabel[key] || formatKey(key);
            docChildren.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: `${label}: `, size: 20, bold: true }),
                        new TextRun({ text: `${formDataValue[key]}`, size: 20 })
                    ],
                })
            );
        });

        // 2. Flatten function
        const flattenData = (obj) => {
            const result = {};
            for (const [key, value] of Object.entries(obj)) {
                if (Array.isArray(value) && value.length && typeof value[0] === 'object') {
                    if (value[0].url) {
                        result[key] = value;
                    } else {
                        value.forEach(item => Object.assign(result, flattenData(item)));
                    }
                } else if (typeof value === 'object' && value !== null) {
                    Object.assign(result, flattenData(value));
                } else {
                    result[key] = value;
                }
            }
            return result;
        };

        // 3. Find array keys
        const arrayKeys = Object.keys(formDataValue).filter(
            key => key !== "submit" && Array.isArray(formDataValue[key]) &&
                formDataValue[key].length && typeof formDataValue[key][0] === 'object'
        );

        // 4. Process each array key
        for (const dynamicWord of arrayKeys) {
            const formattedDynamicWord = keyToLabel[dynamicWord] || formatKey(dynamicWord);

            // Add section title
            docChildren.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: formattedDynamicWord,
                            size: 24,
                            bold: true
                        }),
                    ],
                    spacing: {
                        before: 200,
                        after: 500
                    }
                })
            );

            const allRows = [];
            const allHeaders = new Set();
            const imageData = [];
            let imageColumnKey = null;

            // Collect headers and image data
            formDataValue[dynamicWord].forEach((item, index) => {
                const flat = flattenData(item);
                Object.keys(flat).forEach(k => {
                    if (Array.isArray(flat[k]) && flat[k][0]?.url) {
                        imageColumnKey = k; // Store the image column key
                        imageData[index] = imageData[index] || [];
                        imageData[index].push(...flat[k]);
                    } else {
                        allHeaders.add(k);
                    }
                });
                allRows.push(flat);
            });

            // Only include image column if there is image data
            const hasImages = imageData.some(data => data && data.length > 0);
            const regularColumns = Array.from(allHeaders);
            const sortedHeaders = hasImages ? [...regularColumns, imageColumnKey] : regularColumns;

            const textAreaWidthTwips = 13968;
            const imageColumnWidth = hasImages ? 3400 : 0;
            const regularColumnsCount = regularColumns.length;
            const totalColumnsCount = hasImages ? regularColumnsCount + 1 : regularColumnsCount;

            // Calculate regular column width dynamically
            const totalRegularColumnsWidth = textAreaWidthTwips - imageColumnWidth;
            const regularColumnWidth = regularColumnsCount > 0
                ? totalRegularColumnsWidth / regularColumnsCount
                : 0;

            // Create table headers
            const tableRows = [];
            const headerCells = sortedHeaders.map(header =>
                new TableCell({
                    children: [new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [new TextRun({
                            text: header ? (keyToLabel[header] || formatKey(header)) : '',
                            bold: true
                        })]
                    })],
                    shading: { fill: "DDDDDD" },
                    verticalAlign: VerticalAlign.CENTER,
                })
            );
            tableRows.push(new TableRow({ children: headerCells }));

            // Process data rows
            for (let i = 0; i < allRows.length; i++) {
                const rowData = allRows[i];
                const dataCells = [];

                // Process regular columns
                for (const header of regularColumns) {
                    dataCells.push(
                        new TableCell({
                            children: [new Paragraph({
                                children: [
                                    new TextRun({
                                        text: rowData[header] !== null && rowData[header] !== undefined
                                            ? rowData[header].toString()
                                            : ''
                                    })
                                ]
                            })],
                            verticalAlign: VerticalAlign.CENTER,
                        })
                    );
                }

                // Process image column only if there are images
                if (hasImages) {
                    const imageChildren = [];
                    if (imageData[i] && imageData[i].length) {
                        for (const [index, img] of imageData[i].entries()) {
                            try {
                                const buffer = await fetchImageBuffer(img.url);
                                imageChildren.push(
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new ImageRun({
                                                data: buffer,
                                                transformation: { width: 170, height: 170 }
                                            })
                                        ],
                                        spacing: index < imageData[i].length - 1
                                            ? { after: 200 }
                                            : undefined
                                    })
                                );
                                if (index < imageData[i].length - 1) {
                                    imageChildren.push(
                                        new Paragraph({
                                            text: "",
                                            spacing: { after: 100 }
                                        })
                                    );
                                }
                            } catch (error) {
                                imageChildren.push(
                                    new Paragraph({
                                        text: "Image load error",
                                        alignment: AlignmentType.CENTER
                                    })
                                );
                            }
                        }
                    }
                    dataCells.push(
                        new TableCell({
                            children: imageChildren,
                            verticalAlign: VerticalAlign.CENTER,
                        })
                    );
                }

                tableRows.push(new TableRow({ children: dataCells }));
            }

            // Create column widths
            const columnWidths = hasImages
                ? [...Array(regularColumnsCount).fill(regularColumnWidth), imageColumnWidth]
                : Array(regularColumnsCount).fill(regularColumnWidth);

            // Add table to document
            docChildren.push(
                new Table({
                    rows: tableRows,
                    width: { size: 100, type: "pct" },
                    columnWidths: columnWidths,
                    margins: { top: 100, bottom: 100 },
                })
            );

            // Add spacing between sections
            docChildren.push(new Paragraph({}));
        }

        // Generate and save document with landscape orientation
        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        size: {
                            orientation: PageOrientation.LANDSCAPE
                        },
                    }
                },
                children: docChildren,
            }]
        });

        const blob = await Packer.toBlob(doc);

        // Export To PDF
        if (exportFormat === 'pdf') {
            await convertDocxBlobToPdf(blob);
            await reportGenerated('pdf')
        } else {
            saveAs(blob, filename);
            await reportGenerated('word')
        }
        dispatch(hideLoader());
    };

    const convertDocxBlobToPdf = async (blob, filename = "inspection_data.pdf") => {
        dispatch(showLoader());
        const arrayBuffer = await blob.arrayBuffer();
        const { value: html } = await mammoth.convertToHtml({ arrayBuffer });

        const container = document.createElement("div");

        // Updated CSS with precise image sizing
        const styledHtml = `
        <style>
            body {
                font-family: Arial, sans-serif;
                font-size: 12px;
                padding: 0 15mm;
            }
    
            table {
                border-collapse: collapse;
                width: 100% !important;
                margin: 0 auto;
                table-layout: fixed !important;
            }
    
            th, td {
                border: 1px solid black;
                padding: 6px;
                vertical-align: top;
                word-wrap: break-word;
                overflow-wrap: break-word;
                box-sizing: border-box;
            }
    
            th {
                background-color: #f0f0f0;
                font-weight: bold;
                text-align: center;
            }
    
            tr {
                page-break-inside: avoid;
            }
    
            /* Fixed width for image column */
            td:last-child, th:last-child {
                width: 70mm !important; /* Wider to accommodate 6cm images + padding */
                min-width: 70mm !important;
                max-width: 70mm !important;
                overflow: visible;
                vertical-align: middle;
            }
    
            /* Container for multiple images */
            .image-cell-container {
                display: flex;
                flex-direction: column;
                gap: 5mm;
                align-items: center;
                justify-content: flex-start;
                width: 100%;
                overflow: visible;
            }
    
            /* Individual image container - EXACTLY 6x6 cm */
            .image-container {
                width: 60mm !important;
                height: 60mm !important;
                display: flex;
                justify-content: center;
                align-items: center;
                box-sizing: border-box;
                overflow: visible;
                margin: 0 auto;
                background-color: #f8f8f8; /* Optional: for visual debugging */
                border: 1px dashed #ccc;   /* Optional: for visual debugging */
            }
    
            /* Image styling - ensures full image fits in container */
            .image-container img {
                max-width: 100% !important;
                max-height: 100% !important;
                width: auto !important;
                height: auto !important;
                object-fit: contain !important;
                display: block;
                margin: 0 auto;
                padding: 0;
            }
    
            /* Ensure text columns have flexible width */
            td:not(:last-child), th:not(:last-child) {
                width: auto !important;
                min-width: 30mm;
            }
    
            p {
                margin: 6px 0;
            }
        </style>
        ${html}
        `;

        container.innerHTML = styledHtml;
        document.body.appendChild(container);

        // Process images
        const imageCells = container.querySelectorAll('td:last-child');
        imageCells.forEach(cell => {
            const images = cell.querySelectorAll('img');
            if (images.length > 0) {
                const imageContainer = document.createElement('div');
                imageContainer.className = 'image-cell-container';

                images.forEach(img => {
                    // Remove any fixed dimensions
                    img.removeAttribute('width');
                    img.removeAttribute('height');
                    img.style.width = '';
                    img.style.height = '';

                    // Create container for each image
                    const singleImageContainer = document.createElement('div');
                    singleImageContainer.className = 'image-container';
                    singleImageContainer.appendChild(img);
                    imageContainer.appendChild(singleImageContainer);
                });

                cell.innerHTML = '';
                cell.appendChild(imageContainer);
            }
        });

        // Add delay to ensure images load
        await new Promise(resolve => setTimeout(resolve, 1000));

        await html2pdf().from(container).set({
            margin: 10,
            filename,
            html2canvas: {
                scale: 2,
                useCORS: true,
                logging: true,
                allowTaint: true,
                onclone: (clonedDoc) => {
                    // Enforce critical styles in cloned document
                    const style = clonedDoc.createElement('style');
                    style.innerHTML = `
                        .image-container {
                            width: 60mm !important;
                            height: 60mm !important;
                        }
                        .image-container img {
                            max-width: 100% !important;
                            max-height: 100% !important;
                        }
                    `;
                    clonedDoc.head.appendChild(style);
                }
            },
            jsPDF: {
                unit: "mm",
                format: "a4",
                orientation: "landscape",
                compress: true
            }
        }).save();

        setTimeout(() => document.body.removeChild(container), 1000);
        dispatch(hideLoader());
    };

    const reportGenerated = async (report_type) => {
        const data = {
            inspection_id: inspectionId,
            report_type: report_type,
            is_report_generated: true,
            created_by: userId
        }
        try {
            await inspectionService.reportGenerated(data, token);
        } catch (error) {
            console.error(error)
        }
    }

    const handleExport = () => {
        setShowModalForExcel(true);
    };

    const [showModalForExcel, setShowModalForExcel] = useState(false);
    const [exportFormat, setExportFormat] = useState('excel');

    const handleConfirmExport = () => {
        const filteredValues = {};

        if (formData?.form_data && formValues) {
            formData?.form_data.forEach(field => {
                const key = field.key;

                // Skip the field entirely if its tableView is false
                if (field?.tableView === false) return;

                const originalValue = formValues[key];

                // No components — include directly
                if (!Array.isArray(field.components)) {
                    filteredValues[key] = originalValue;
                    return;
                }

                // Filter components with tableView !== false
                const visibleComponents = field.components.filter(comp => comp.tableView !== false);
                if (visibleComponents.length === 0) return;

                // Handle arrays
                if (Array.isArray(originalValue)) {
                    const cleaned = originalValue.map(entry => {
                        const filtered = {};
                        visibleComponents.forEach(comp => {
                            if (entry.hasOwnProperty(comp.key)) {
                                filtered[comp.key] = entry[comp.key];
                            }
                        });
                        return filtered;
                    });
                    filteredValues[key] = cleaned;
                }

                // Handle objects
                else if (typeof originalValue === "object" && originalValue !== null) {
                    const filtered = {};
                    visibleComponents.forEach(comp => {
                        if (originalValue.hasOwnProperty(comp.key)) {
                            filtered[comp.key] = originalValue[comp.key];
                        }
                    });
                    filteredValues[key] = filtered;
                }

                // Primitive value (fallback)
                else {
                    filteredValues[key] = originalValue;
                }
            });
        }

        if (exportFormat === 'excel') {
            exportToExcelWithImages(filteredValues);
        } else if (exportFormat === 'word' || exportFormat === 'pdf') {
            exportToWordWithImages(filteredValues)
        }
    }

    // Helper function to create a key-to-label mapping
    const createKeyToLabelMap = (formData) => {
        const keyToLabel = {};

        const collectLabels = (components) => {
            components?.forEach(comp => {
                if (comp.key && comp.label) {
                    keyToLabel[comp.key] = comp.label;
                }

                // Recursively collect from nested structures
                if (Array.isArray(comp.components)) {
                    collectLabels(comp.components);
                }
                if (Array.isArray(comp.columns)) {
                    comp.columns.forEach(col => collectLabels(col.components || []));
                }
                if (Array.isArray(comp.rows)) {
                    comp.rows.forEach(row => row.forEach(cell => collectLabels(cell.components || [])));
                }
                if (Array.isArray(comp.fieldSet)) {
                    collectLabels(comp.fieldSet);
                }
                if (Array.isArray(comp.tabs)) {
                    comp.tabs.forEach(tab => collectLabels(tab.components || []));
                }
            });
        };

        formData?.form_data?.forEach(field => {
            if (field.key && field.label) {
                keyToLabel[field.key] = field.label;
            }

            if (Array.isArray(field.components)) {
                collectLabels(field.components);
            }
        });

        return keyToLabel;
    };



    // Export Excel Functionality End

    const [showModal, setShowModal] = useState(false);
    const [selectedField, setSelectedField] = useState('');


    const extensionMap = {
        "application/pdf": ".pdf",
        "image/png": ".png",
        "image/jpeg": ".jpg",
        "image/jpg": ".jpg",
        "image/webp": ".webp"
    };

    // Recursively find the first value by key
    const findValueByKey = (obj, targetKey) => {
        if (Array.isArray(obj)) {
            for (const item of obj) {
                const result = findValueByKey(item, targetKey);
                if (result !== null && result !== undefined) return result;
            }
        } else if (typeof obj === "object" && obj !== null) {
            for (const key in obj) {
                if (key === targetKey && typeof obj[key] !== "object") {
                    return obj[key];
                }

                const result = findValueByKey(obj[key], targetKey);
                if (result !== null && result !== undefined) return result;
            }
        }

        return null;
    };



    // Recursively find all file URLs
    const findAllFileUrls = async (obj, found = []) => {
        if (Array.isArray(obj)) {
            for (const item of obj) {
                await findAllFileUrls(item, found);
            }
        } else if (typeof obj === "object" && obj !== null) {
            if (obj.url && typeof obj.url === "string") {
                const isBlobUrl = obj.url.startsWith("https://");
                const isBase64Url = obj.url.startsWith("data:");

                if (isBlobUrl || isBase64Url) {
                    const fileName = obj.originalName || obj.name || (isBlobUrl
                        ? obj.url.split("?")[0].split("/").pop()
                        : `file-${Date.now()}`); // fallback for base64

                    found.push({ url: obj.url, name: fileName });
                }
            }

            for (const value of Object.values(obj)) {
                await findAllFileUrls(value, found);
            }
        }

        return found;
    };


    const downloadAllFilesIntoZip = async (groupKey, selectedFieldKey) => {
        const zip = new JSZip();
        const nameCountMap = {};

        if (!formValues[groupKey] || !Array.isArray(formValues[groupKey])) {
            alert(`❌ No valid array found for: ${groupKey}`);
            return;
        }

        const records = formValues[groupKey];

        for (const record of records) {
            const folderName = findValueByKey(record, selectedFieldKey);

            if (!folderName || typeof folderName !== "string") {
                await showToast("warning", `Skipping record — No valid folder name for key: ${selectedFieldKey}`)
                continue;
            }

            const fileEntries = await findAllFileUrls(record);

            for (const file of fileEntries) {
                await addFileToZip(zip, folderName, file);
            }
        }

        // ✅ Add top-level image-only fields (outside the groupKey)
        for (const topKey in formValues) {
            if (topKey === groupKey) continue;

            const value = formValues[topKey];
            if (Array.isArray(value) && value.length && typeof value[0] === "object" && value[0].url) {
                const type = value[0].type || "";
                if (type.startsWith("image/") || type === "application/pdf") {
                    for (const file of value) {
                        await addFileToZip(zip, topKey, file);
                    }
                }
            }
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, "grouped-files.zip");
    };


    const addFileToZip = async (zip, folderName, file) => {
        try {
            // Item 8: private container — fetch over the signed link.
            const response = await fetch(await resolveSignedUrl(file.url));
            if (!response.ok) {
                await showToast("error", `Failed to fetch ${file.url}`);
                return;
            }

            const blob = await response.blob();
            if (blob.size < 1000) {
                await showToast("warning", `⚠️ Blob too small, skipping ${file.url}`);
                return;
            }

            const contentType = response.headers.get("Content-Type") || "";
            const correctExt = extensionMap[contentType.toLowerCase()] || "";
            let fileName = file.originalName ? file.originalName : file.name;

            const currentExt = fileName.includes(".")
                ? fileName.slice(fileName.lastIndexOf("."))
                : "";

            if (correctExt && currentExt.toLowerCase() !== correctExt) {
                fileName = fileName.replace(/\.[^/.]+$/, "") + correctExt;
            }

            // No renaming logic — will overwrite if same name is used
            zip.folder(folderName)?.file(fileName, blob);
        } catch (err) {
            await showToast("error", `Failed to fetch ${file.url}`);
            // console.error(`❌ Error downloading ${file.url}`, err);
        }
    };



    const [selectedFields, setSelectedFields] = useState({});

    const extractFlatNonFileFieldsWithLabels = (formValues, keyToLabelMap) => {
        const result = {};

        const isImage = (obj) =>
            typeof obj?.url === "string" && obj.url.startsWith("data:image/");

        const containsImageDeep = (obj) => {
            if (Array.isArray(obj)) {
                return obj.some(item => containsImageDeep(item));
            } else if (typeof obj === 'object' && obj !== null) {
                if (isImage(obj)) return true;
                return Object.values(obj).some(val => containsImageDeep(val));
            }
            return false;
        };

        const collectImageKeys = (obj, keys) => {
            if (typeof obj === 'object' && obj !== null && 'url' in obj) {
                // ❌ Skip objects with 'url' key entirely
                return;
            }

            for (const key in obj) {
                const value = obj[key];

                if (Array.isArray(value)) {
                    if (value.length && typeof value[0] === 'object' && containsImageDeep(value)) {
                        collectImageKeys(value[0], keys);
                    }
                } else if (typeof value === 'object' && value !== null) {
                    if (containsImageDeep(value)) {
                        collectImageKeys(value, keys);
                    }
                } else {
                    keys.add(key); // ✅ Only primitive, not from file object
                }
            }
        };

        for (const topKey in formValues) {
            const val = formValues[topKey];

            if (containsImageDeep(val)) {
                const keys = new Set();
                if (Array.isArray(val) && val.length && typeof val[0] === 'object') {
                    collectImageKeys(val[0], keys);
                    result[topKey] = [...keys].map(k => ({
                        key: k,
                        label: keyToLabelMap[k] || k
                    }));
                } else if (typeof val === 'object' && val !== null) {
                    collectImageKeys(val, keys);
                    result[topKey] = [...keys].map(k => ({
                        key: k,
                        label: keyToLabelMap[k] || k
                    }));
                }
            }
        }

        return result;
    };

    const keyToLabelMap = createKeyToLabelMap(formData);
    const groupedFields = extractFlatNonFileFieldsWithLabels(formValues, keyToLabelMap);

    // const groupedFields = getFileFieldGroups(formData?.form_data || []);

    const extractImagesGroupedByTopLevel = (formValues, keyToLabel) => {
        const result = {};

        // Item 8: some iPad-synced photo entries carry no `type` — requiring it
        // silently dropped them from the gallery. Fall back to the file
        // extension in the url/name when type is absent.
        const looksLikeImagePath = (value) =>
            typeof value === 'string' &&
            /\.(jpe?g|png|gif|webp|heic|heif)(\?.*)?$/i.test(value);

        const isImage = (item) =>
            item &&
            typeof item === 'object' &&
            item.url &&
            (item.type
                ? item.type.startsWith('image/') || item.type === 'application/pdf'
                : looksLikeImagePath(item.url) || looksLikeImagePath(item.name));

        const collectImages = (obj, groupKey) => {
            if (!obj || typeof obj !== 'object') return;

            for (const key in obj) {
                const value = obj[key];

                if (Array.isArray(value)) {
                    value.forEach((entry) => {
                        if (isImage(entry)) {
                            if (!result[groupKey]) result[groupKey] = [];
                            result[groupKey].push(entry);
                        } else if (typeof entry === 'object') {
                            collectImages(entry, groupKey);
                        }
                    });
                } else if (typeof value === 'object') {
                    collectImages(value, groupKey);
                }
            }
        };

        for (const topKey in formValues) {
            const section = formValues[topKey];
            if (Array.isArray(section)) {
                section.forEach((row, index) => {
                    const readableLabel = keyToLabel[topKey] || topKey;
                    const groupKey = `${readableLabel} Row ${index + 1}`;
                    collectImages(row, groupKey);
                });
            }
        }

        return result;
    };


    const keyToLabel = createKeyToLabelMap(formData); // your formData with labels
    const imagesByRow = extractImagesGroupedByTopLevel(formValues, keyToLabel);


    useEffect(() => {
        if (id) {
            getTemplate();
        }
    }, [id, getTemplate]);

        // get inspection details by their id
        const getInspection = async () => {
            dispatch(showLoader());
            try {
                const response = await inspectionService.getInspection(inspectionId, token)
                if (response?.data?.statusCode === 200) {
                    setInspection(response?.data?.body?.data?.inspection[0])
                } else {
                    showToast("error", response?.data?.body?.message)
                }
    
            } catch (error) {
                await HandleApiError(error)
            } finally {
                dispatch(hideLoader())
            }
        }
    
        useEffect(() => {
            getInspection()
        }, [inspectionId])

    useEffect(() => {
        const notifyInspectionStarted = async () => {
            if (!inspectionId || !userId || !token) return;
            try {
                await inspectionService.inspectionStarted({
                    inspector_id: userId,
                    inspection_id: inspectionId,
                    source: 'web',
                }, token);
            } catch (error) {
                console.error('inspectionStarted error:', error);
            }
        };
        notifyInspectionStarted();
    }, [inspectionId, userId, token]);

    const handleFormChange = (changedData) => {
        if (changedData?.data?.submit) {
            setIsReponseExist(true)
        }
        // Ignore empty or initialization-related changes
        if (changedData.changed && Object.keys(changedData.data).length > 0) {
            setFormValues((prevValues) => ({
                ...prevValues,
                ...changedData.data,
            }));
        }
    };

    useEffect(() => {
        return () => {
            if (imageElement?.src) {
                URL.revokeObjectURL(imageElement.src);
            }
        };
    }, [imageElement]);

    const normalizeImageCell = (cellValue, fieldName) => {
        if (!cellValue || typeof cellValue !== "string") return [];

        return cellValue
            .split(",")
            .map(v => v.trim())
            .filter(v => v.startsWith("http") || v.startsWith("data:image"))
            .map((url, index) => ({
                storage: url.startsWith("data:image") ? "base64" : "url",
                name: `${fieldName}_${index + 1}`,
                size: 0,
                type: url.includes("png") ? "image/png" : "image/jpeg",
                url,
                originalName: `${fieldName}_${index + 1}`,
                hash: ""
            }));
    };

    const handleImport = async (e) => {
        console.log("check__to_push_code");
        const file = e.target.files[0];
        if (!file) return;
      
        const reader = new FileReader();
      
        const findEditGrids = (components = []) => {
          const grids = [];
          components.forEach((comp) => {
            if (!comp) return;
      
            if (comp.type === "editgrid") {
              grids.push({
                key: comp.key,
                childKeys: (comp.components || []).map(c => c.key).filter(Boolean),
              });
            }
      
            if (Array.isArray(comp.components)) {
              grids.push(...findEditGrids(comp.components));
            }
      
            if (Array.isArray(comp.columns)) {
              comp.columns.forEach(col => {
                if (Array.isArray(col.components)) {
                  grids.push(...findEditGrids(col.components));
                }
              });
            }
      
            if (Array.isArray(comp.rows)) {
              comp.rows.forEach(row =>
                row.forEach(cell => {
                  if (Array.isArray(cell.components)) {
                    grids.push(...findEditGrids(cell.components));
                  }
                })
              );
            }
          });
          return grids;
        };
      
        reader.onload = (evt) => {
          try {
            const workbook = XLSX.read(evt.target.result, { type: "binary" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      
            const editGrids = findEditGrids(formSchema.components || []);
            const mainGrid = editGrids[0];
            const subGrids = editGrids.slice(1);
      
            const mainKey = mainGrid.key;
            const locationKey = subGrids[0].key;
            const itemKey = subGrids[1].key;
      
            const buildingsArr = [];
            const locationsMap = {}; // 🔥 DEDUP
            const itemsArr = [];
      
            let serialNo = 1;
      
            rows.forEach(row => {
              const buildingName = String(row[mainGrid.childKeys[0]]).trim();
              if (!buildingName) return;
      
              if (!buildingsArr.find(b => b.buildingName === buildingName)) {
                buildingsArr.push({ buildingName });
              }
      
              // 🔑 LOCATION KEY
              const locKey = `${buildingName}|${row.level}|${row.observationArea}|${row.unitCommonPropertyLocation}`;
      
              if (!locationsMap[locKey]) {
                locationsMap[locKey] = {
                  building: buildingName,
                  level: row.level,
                  observationArea: row.observationArea,
                  unitCommonPropertyLocation: String(row.unitCommonPropertyLocation),
                  
                  itemCount: 0,
                };
              }
      
              const locationIndex = Object.keys(locationsMap).indexOf(locKey);
      
              itemsArr.push({
                area: row.areaDropdown || "",
                areaDropdown: row.areaDropdown || "",
                itemCategory: row.itemCategory || "",
                description: row.description || row.itemCategory || "",
                breach: row.breach || "",
                cause: row.cause || "",
                scopeOfRectification: row.scopeOfRectification || "",
                reinspectionDescription: row.reinspectionDescription || "",
                serialNo: serialNo++,
                location: `${buildingName}.${row.level}.${row.observationArea}.${row.unitCommonPropertyLocation}`,
                itemNumber: `${buildingName}.${row.level}.${row.observationArea}.${row.unitCommonPropertyLocation}.${locationsMap[locKey].itemCount + 1}`,
                locationIndex,
                currentLocation: "",
                photos: normalizeImageCell(row.photos, "photos"),
                rectifiedPhotos: normalizeImageCell(row.rectifiedPhotos, "rectifiedPhotos"),
                rectificationStatus: "",
                // itemCaptureTime: dayjs().format("DD-MM-YYYY hh:mm:ss A"),
                itemCategoryIdentifier: row.itemCategory || "",
              });
      
              locationsMap[locKey].itemCount += 1;
            });
      
            const locationsArr = Object.values(locationsMap);
      
            const formValues = {
              numberOfBuildings: buildingsArr.length,
              buildings: buildingsArr,
              buildingName: null,
              buildingUID_master: "",
      
              numberOfLocations: locationsArr.length,
              locations: locationsArr,
      
              locationIndex: null,
              currentLocation: null,
              building: null,
              itemCount: null,
              level: null,
              observationArea: null,
              unitCommonPropertyLocation: null,

      
              selectedBuilding: buildingsArr[0]?.buildingName || "",
              selectBuilding: "",
      
              numberOfItems: itemsArr.length,
              items: itemsArr,
      
              itemCaptureTime: null,
              serialNo: null,
              location: null,
              itemNumber: null,
              areaDropdown: null,
              area: null,
              itemCategoryIdentifier: null,
              itemCategory: null,
              cause: null,
              breach: null,
              scopeOfRectification: null,
              description: null,
              photos: null,
              status: null,
              rectificationStatus: null,
              reinspectionDescription: null,
              rectifiedPhotos: null,
      
              selectLocation: "",
              submit: true,
            };
      
            setFormValues(formValues);
            console.log("Imported formValues:", formValues);
          } catch (err) {
            console.error("Import error:", err);
          }
        };
      
        reader.readAsBinaryString(file);
      };

    return (
        <>
            <div className="main-inner-block">
                <div className="heading-content">Form</div>
                <div className="content">

                    {isReponseExist && (

                        <div className='d-flex align-items-center justify-content-between mb-4 block-one gap-3'>
                            <button
                                type='button'
                                className="green-btn green-signin addnewuser excel-icon"
                                onClick={handleExport}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                                    <path fill="#217346" d="M4 2h10l6 6v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                                    <path fill="#fff" d="M14 3.5V8h4.5L14 3.5z" />
                                    <path fill="#fff" d="M8 10h1.2l1.1 2.1L11.5 10H13l-1.7 3 1.8 3h-1.5l-1.2-2.2-1.2 2.2H7.8l1.8-3L8 10z" />
                                </svg>
                                Export As
                            </button>

                            {showModalForExcel && (
                                <div className="modal-overlay">
                                    <div className="modal-box">
                                        <h3>Export As</h3>
                                        <select
                                            className="export-select"
                                            value={exportFormat}
                                            onChange={(e) => setExportFormat(e.target.value)}
                                        >
                                            <option value="excel">Excel</option>
                                            <option value="word">Word</option>
                                            <option value="pdf">PDF</option>
                                        </select>
                                        <div className="modal-actions">
                                            <button onClick={handleConfirmExport} className="green-btn">Export</button>
                                            <button onClick={() => setShowModalForExcel(false)} className="gray-btn">Cancel</button>
                                        </div>
                                    </div>
                                </div>
                            )}


                            <button
                                type='button'
                                className="green-btn green-signin addnewuser download-icon"
                                onClick={() => setShowModal(true)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M5 20h14v-2H5v2zm7-2l5-5h-3V4h-4v9H7l5 5z" />
                                </svg>
                                Download Images
                            </button>

                            {showModal && (
                                <div className="custom-modal-overlay">
                                    <div className="custom-modal">
                                        <h2 className="modal-title">Download Grouped Images</h2>
                                        {/* <p className="modal-description">
                                            Select the field you want to use for grouping images in folders:
                                        </p> */}

                                        {Object.entries(groupedFields)
                                            .filter(([_, fields]) => Array.isArray(fields) && fields.length > 0)
                                            .map(([groupKey, fields]) => (
                                                <div key={groupKey} className="edit-grid-group">
                                                    <h4>Select field from <strong>{keyToLabelMap[groupKey] || groupKey}</strong></h4>

                                                    <select
                                                        className="modal-select"
                                                        value={selectedFields[groupKey] || ""}
                                                        onChange={e =>
                                                            setSelectedFields(prev => ({ ...prev, [groupKey]: e.target.value }))
                                                        }
                                                    >
                                                        <option value="">-- Select Field --</option>
                                                        {fields.map((field, idx) => (
                                                            <option key={idx} value={field.key}>
                                                                {field.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <div className="modal-buttons">
                                                        <button
                                                            className="btn primary"
                                                            disabled={!selectedFields[groupKey]}
                                                            onClick={() => downloadAllFilesIntoZip(groupKey, selectedFields[groupKey])}
                                                        >
                                                            Download ZIP
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}



                                        <button className="btn primary cancel" onClick={() => setShowModal(false)}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}


                        </div>
                    )}



                    <div className="white-box">

                        {/* Import Button */}
                        <div style={{ marginBottom: "10px" }}>
                            <label
                                htmlFor="fileImport"
                                className="green-btn green-signin addnewuser excel-icon"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                                    <path fill="#217346" d="M4 2h10l6 6v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                                    <path fill="#fff" d="M14 3.5V8h4.5L14 3.5z" />
                                    <path fill="#fff" d="M8 10h1.2l1.1 2.1L11.5 10H13l-1.7 3 1.8 3h-1.5l-1.2-2.2-1.2 2.2H7.8l1.8-3L8 10z" />
                                </svg>
                                Import Excel
                            </label>
                            <input
                                id="fileImport"
                                type="file"
                                accept=".csv, .xlsx, .xls"
                                style={{ display: "none" }}
                                onChange={handleImport}
                            />
                        </div>

                        <Form
                            ref={formRef}
                            form={formSchema}
                            submission={{ data: formValues }}
                            onChange={handleFormChange}
                            onSubmit={handleSubmit}
                            options={{
                                noAlerts: true,
                                hideSubmitButton: false,
                                fileService,
                            }}
                        />

                        {/* Display all found images */}
                        <div className="image-preview">
                            {Object.entries(imagesByRow).map(([group, images]) => (
                                <div key={group} className="image-group">
                                    <h6><b>{group}:</b></h6>
                                    <div className="image-row">
                                        {images.map((image, index) => (
                                            <div key={index} className="image-container">
                                                {image.type === 'application/pdf' ? (
                                                    <a href={displayUrl(image.url)} target="_blank" rel="noopener noreferrer">
                                                        <img src="/pdf-icon.png" alt="PDF Preview" className="preview-image" />
                                                    </a>
                                                ) : (
                                                    <img
                                                        src={displayUrl(image.url)}
                                                        alt={image.originalName}
                                                        className="preview-image"
                                                        onClick={() => setSelectedImage(image)}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                )}
                                                <p>{image.originalName}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>


                        {selectedImage && (
                            <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
                                <div className="modal-content-preview" onClick={(e) => e.stopPropagation()}>
                                    <img src={displayUrl(selectedImage.url)} alt={selectedImage.originalName} style={{ maxWidth: '100%', maxHeight: '80vh' }} />
                                    <p>{selectedImage.originalName}</p>
                                    <button type="button"
                                        className="green-btn submit-btn view-btn" onClick={() => setSelectedImage(null)}>Close</button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div >
            <div className="text-center d-flex justify-content-center button-block gap-3">
                <button
                    type="button"
                    className="green-btn submit-btn view-btn"
                    onClick={() => navigate(-1)}
                >
                    Back
                </button>
            </div>

            {/* Annotation Code Start */}

            {
                showAnnotationModal && (
                    <div className="annotation-modal">
                        <div className="annotation-modal-content">
                            <h3>Annotate {currentFile?.type === 'application/pdf' ? 'PDF' : 'Image'}</h3>
                            <div className="shape-selection-toolbar">
                                <div
                                    className={`shape-btn ${selectedShape === 'rectangle' ? 'active' : ''}`}
                                    onClick={() => setSelectedShape('rectangle')}
                                    title="Rectangle"
                                >
                                    <FaSquare />
                                </div>
                                <div
                                    className={`shape-btn ${selectedShape === 'circle' ? 'active' : ''}`}
                                    onClick={() => setSelectedShape('circle')}
                                    title="Circle"
                                >
                                    <FaCircle />
                                </div>
                                <div
                                    className={`shape-btn ${selectedShape === 'arrow' ? 'active' : ''}`}
                                    onClick={() => setSelectedShape('arrow')}
                                    title="Arrow"
                                >
                                    <FaArrowRight />
                                </div>
                                <div
                                    className={`shape-btn ${selectedShape === 'pencil' ? 'active' : ''}`}
                                    onClick={() => setSelectedShape('pencil')}
                                    title="Pencil"
                                >
                                    <FaPencilAlt />
                                </div>
                                <div
                                    className={`shape-btn ${selectedShape === 'text' ? 'active' : ''}`}
                                    onClick={() => setSelectedShape('text')}
                                >
                                    <FaFont />
                                </div>
                                <input
                                    type="color"
                                    value={selectedColor}
                                    onChange={(e) => setSelectedColor(e.target.value)}
                                    title="Select Color"
                                    className="color-picker"
                                />
                            </div>
                            <div className="file-info">
                                {currentFile && `File: ${currentFile.name} (${Math.round(currentFile.size / 1024)} KB)`}
                            </div>
                            <div className="annotation-instructions">Click and drag to create annotations</div>
                            <div className="annotation-stage-container">
                                <Stage
                                    ref={stageRef}
                                    width={imageSize.width}
                                    height={imageSize.height}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                >
                                    <Layer>
                                        {imageElement && (
                                            <KonvaImage image={imageElement} width={imageSize.width} height={imageSize.height} />
                                        )}
                                        {annotations.map((annotation) => (
                                            <React.Fragment key={annotation.id}>
                                                {annotation.shape === 'text' ? (
                                                    <Text
                                                        x={annotation.x}
                                                        y={annotation.y}
                                                        text={annotation.text}
                                                        fontSize={16}
                                                        fill={annotation.color}
                                                    />
                                                ) : annotation.shape === 'rectangle' ? (
                                                    <>
                                                        <Rect
                                                            x={annotation.x}
                                                            y={annotation.y}
                                                            width={annotation.width}
                                                            height={annotation.height}
                                                            stroke={annotation.color}
                                                            strokeWidth={2}
                                                        />
                                                        <Text
                                                            x={annotation.x}
                                                            y={annotation.y - 20}
                                                            text={annotation.text}
                                                            fontSize={16}
                                                            fill={annotation.color}
                                                        />
                                                    </>
                                                ) : annotation.shape === 'circle' ? (
                                                    <>
                                                        <Circle
                                                            x={annotation.x}
                                                            y={annotation.y}
                                                            radius={annotation.radius || 50}
                                                            stroke={annotation.color}
                                                            strokeWidth={2}
                                                        />
                                                        <Text
                                                            x={annotation.x}
                                                            y={annotation.y - (annotation.radius || 50) - 20}
                                                            text={annotation.text}
                                                            fontSize={16}
                                                            fill={annotation.color}
                                                        />
                                                    </>
                                                ) : annotation.shape === 'arrow' ? (
                                                    <>
                                                        <Arrow
                                                            points={[annotation.x, annotation.y, annotation.endX, annotation.endY]}
                                                            stroke={annotation.color}
                                                            strokeWidth={2}
                                                            pointerLength={10}
                                                            pointerWidth={10}
                                                        />
                                                        <Text
                                                            x={(annotation.x + annotation.endX) / 2}
                                                            y={(annotation.y + annotation.endY) / 2 - 20}
                                                            text={annotation.text}
                                                            fontSize={16}
                                                            fill={annotation.color}
                                                        />
                                                    </>
                                                ) : (
                                                    <Line
                                                        points={annotation.points}
                                                        stroke={annotation.color}
                                                        strokeWidth={2}
                                                        tension={0.5}
                                                        lineCap="round"
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {currentAnnotation && (
                                            <>
                                                {selectedShape === 'rectangle' ? (
                                                    <Rect
                                                        x={currentAnnotation.x}
                                                        y={currentAnnotation.y}
                                                        width={currentAnnotation.width}
                                                        height={currentAnnotation.height}
                                                        stroke={selectedColor}
                                                        strokeWidth={2}
                                                        dash={[5, 5]}
                                                    />
                                                ) : selectedShape === 'circle' ? (
                                                    <Circle
                                                        x={currentAnnotation.x}
                                                        y={currentAnnotation.y}
                                                        radius={circleRadius}
                                                        stroke={selectedColor}
                                                        strokeWidth={2}
                                                        dash={[5, 5]}
                                                    />
                                                ) : selectedShape === 'arrow' ? (
                                                    <Arrow
                                                        points={[
                                                            currentAnnotation.x,
                                                            currentAnnotation.y,
                                                            currentAnnotation.endX || currentAnnotation.x + 50,
                                                            currentAnnotation.endY || currentAnnotation.y + 50,
                                                        ]}
                                                        stroke={selectedColor}
                                                        strokeWidth={2}
                                                        dash={[5, 5]}
                                                        pointerLength={10}
                                                        pointerWidth={10}
                                                    />
                                                ) : null}
                                            </>
                                        )}
                                    </Layer>
                                </Stage>
                            </div>
                            <div className="annotation-modal-buttons">
                                {currentFile?.type === 'application/pdf' && (
                                    <div className="pdf-navigation">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                if (currentPage > 1) {
                                                    setCurrentPage(currentPage - 1);
                                                    loadPDF(currentFile, currentPage - 1);
                                                }
                                            }}
                                            disabled={currentPage === 1 || isProcessing}
                                        >
                                            Previous Page
                                        </button>
                                        <span style={{ padding: '2px' }}>Page {currentPage} of {totalPages}</span>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                if (currentPage < totalPages) {
                                                    setCurrentPage(currentPage + 1);
                                                    loadPDF(currentFile, currentPage + 1);
                                                }
                                            }}
                                            disabled={currentPage === totalPages || isProcessing}
                                        >
                                            Next Page
                                        </button>
                                    </div>
                                )}
                                <button className="btn btn-secondary" onClick={undoAnnotation} disabled={annotations.length === 0 || isProcessing}>
                                    Undo
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => handleAnnotationComplete(false)}
                                    disabled={isProcessing}
                                >
                                    Skip
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleAnnotationComplete(true)}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? 'Processing...' : 'Save & Next'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
};


export default StartInspection
